import { useMemo, useState, type FunctionComponent } from 'react'
import { formatDate } from '../../const/DateFormat'
import { ellipsedText } from '../../const/EllipsedTextHelper'
import { labelScore } from '../../const/ScoreHelper'
import { useTranslate } from '../../contexts/TranslationContext'
import type { BlogPostReviewResult } from '../../data/BlogPostReviewFragment'
import type { ProductReviewResult } from '../../data/ProductReviewFragment'
import SvgChevronDown from '../../svg/icons/ChevronDown'
import { useAgesOptions } from '../dogForm/DogInfoForm'
import { Button } from './Button'
import { Logo } from './Logo'
import style from './ReviewCard.module.sass'
import { ReviewRating } from './ReviewRating'
import { Tag } from './Tag'

type ReviewCardProps = {
	review: BlogPostReviewResult | ProductReviewResult
	openLoginModal: () => void
}

export const ReviewCard: FunctionComponent<ReviewCardProps> = ({ review, openLoginModal }) => {
	const [isTextComplete, setIsTextComplete] = useState(false)
	const lastCharacterVisible = 280
	const extraLongReview = review.text && review.text.length > 300

	const ellipsedReview = useMemo(() => {
		if (extraLongReview) {
			const shortenedText = ellipsedText(review.text, lastCharacterVisible)
			return shortenedText
		}
		return review.text
	}, [extraLongReview, review.text])

	const translate = useTranslate()

	const ages = useAgesOptions(review.breed?.id)
	const ageItem = ages.data?.find((item) => item.id === review.age?.id)

	return (
		<>
			<div className={style.root}>
				<div className={style.header}>
					{'title' in review && review.title && <div className={style.title}>{review.title}</div>}
					{'score' in review && review.score && (
						<div className={style.scoreWrapper}>
							<Tag
								color={Number(review.score.starsAmount) > 2 ? 'greenLight' : 'pink'}
								label={review.score.label ?? translate('reviewCard.notSpecified')}
								isLarger
							/>
							<div className={style.scoreRange}>{labelScore(Number(review.score.starsAmount))}</div>
						</div>
					)}
					{review.publishedAt && <div className={style.date}>{formatDate(review.publishedAt)}</div>}
				</div>
				<div className={style.text}>
					{isTextComplete && extraLongReview ? review.text : ellipsedReview}
					{!isTextComplete && extraLongReview && (
						<div className={style.buttonWrapper}>
							<Button
								type={{ variant: 'naked' }}
								label={translate('reviewCard.button.readMore')}
								iconAfter={<SvgChevronDown />}
								icon={{ fillPrimary: true }}
								onClick={() => setIsTextComplete(true)}
							/>
						</div>
					)}
				</div>
				<div className={style.foot}>
					<div className={style.meta}>
						<div className={style.metaIcon}>
							{review.fullName?.charAt(0).toUpperCase() || review.user?.email.charAt(0).toUpperCase()}
						</div>
						<div className={style.metaInfo}>
							<div className={style.metaName}>{review.fullName || translate('reviewCard.anonymous')}</div>
							<div className={style.dogListWrapper}>
								{review.breed?.name}, {ageItem?.label.split(' (')[0].toLowerCase()}
							</div>
						</div>
					</div>
					<div className={style.rating}>
						<ReviewRating review={review} openLoginModal={openLoginModal} />
					</div>
				</div>
				{review.response && (
					<div className={style.response}>
						<div className={style.responseHeader}>
							<span className={style.responseMeta}>{translate('reviewCard.response')}</span>
						</div>
						<span className={style.responseText}>{review.response}</span>
						<Logo small />
					</div>
				)}
			</div>
		</>
	)
}
